import { useState, type KeyboardEvent } from "react";
import { useField } from "formik";
import { IconCheck, IconPlus } from "@tabler/icons-react";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Text from "../UI/Text";
import styles from "./EdlTagChips.module.css";

interface EdlTagChipsProps {
  name: string;
  label: string;
  /** Valeurs proposées (celles déjà cochées hors de cette liste s'y ajoutent). */
  options: string[];
}

/** Sélection multiple par pastilles à cocher, avec saisie d'une valeur libre. */
export default function EdlTagChips({
  name,
  label,
  options,
}: EdlTagChipsProps) {
  const [{ value }, , { setValue }] = useField<string[]>(name);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const selected = value ?? [];
  const choices = [
    ...options,
    ...selected.filter((item) => !options.includes(item)),
  ];

  function toggle(item: string) {
    setValue(
      selected.includes(item)
        ? selected.filter((v) => v !== item)
        : [...selected, item],
    );
  }

  function commitDraft() {
    const trimmed = draft.trim();
    if (trimmed && !selected.includes(trimmed)) {
      setValue([...selected, trimmed]);
    }
    setDraft("");
    setAdding(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commitDraft();
    } else if (event.key === "Escape") {
      setDraft("");
      setAdding(false);
    }
  }

  return (
    <div className={styles.field}>
      <Text variant="deci" weight="bold" as="div">
        {label}
      </Text>
      <div className={styles.chips} role="group" aria-label={label}>
        {choices.map((item) => {
          const checked = selected.includes(item);
          return (
            <button
              key={item}
              type="button"
              aria-pressed={checked}
              className={classnames(styles.chip, checked && styles.checked)}
              onClick={() => toggle(item)}
            >
              {checked && <IconCheck size={14} stroke={2.5} />}
              {item}
            </button>
          );
        })}
        {adding ? (
          <TextInputStandalone
            name={`${name}-autre`}
            placeholder="Autre…"
            value={draft}
            autoFocus
            className={styles.draft}
            onChange={(event: { target: { value: string } }) =>
              setDraft(event.target.value)
            }
            onBlur={commitDraft}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <button
            type="button"
            className={classnames(styles.chip, styles.addChip)}
            onClick={() => setAdding(true)}
          >
            <IconPlus size={14} stroke={2.5} />
            Autre
          </button>
        )}
      </div>
    </div>
  );
}
